import { useCallback } from 'react';
import React from 'react';

import { Icon } from '@rbilabs/universal-components';
import { Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { Skeleton } from 'native-base';
import { useIntl } from 'react-intl';
import { StyleProp, StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';
import { spacing } from 'ucl/themes/shared/primitives';

import { Helmet } from 'components/helmet';
import { primitive } from 'styles/constants/primitives';
import { isIos } from 'utils/environment';
import { routes } from 'utils/routing';

export interface ModalHeaderProps {
  headerText?: string;
  onDismiss?: () => void;
  dismissType?: 'close' | 'back';
  invertHeaderColor?: boolean;
  /** this is the title for the helmet tag if different from auto-derived header text*/
  helmetTitle?: string;
  customHeaderLeft?: React.ReactNode;
  customHeaderRight?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  isLoading?: boolean;
  /** mostly for cart that we don't want the rounding, and its a full-screen takeover */
  isFullScreen?: boolean;
}

export const STANDARD_BACKGROUND = primitive.bk.$mayoEggWhite;

export const CardModalHeader: React.FC<ModalHeaderProps> = ({
  headerText,
  onDismiss,
  dismissType = 'close',
  helmetTitle,
  invertHeaderColor = false,
  customHeaderLeft,
  isLoading,
  customHeaderRight,
  containerStyle,
  isFullScreen,
}) => {
  const { formatMessage } = useIntl();
  const modalDismiss = useGetModalDismiss();

  if (!onDismiss) {
    onDismiss = modalDismiss;
  }

  const invertFontStyle = invertHeaderColor && { color: primitive.bk.$mayoEggWhite };

  return (
    <View
      style={[
        modalHeaderStyles.headerContainer,
        invertHeaderColor
          ? {
              backgroundColor: primitive.bk.$bbqBrown,
            }
          : { backgroundColor: STANDARD_BACKGROUND },
        isFullScreen ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : null,
        containerStyle,
      ]}
    >
      {(helmetTitle || headerText) && <Helmet title={helmetTitle || (headerText as any)} />}
      <View style={modalHeaderStyles.headerFlexStart}>
        {customHeaderLeft ||
          (dismissType === 'back' && (
            <TouchableOpacity
              onPress={() => onDismiss?.()}
              testID="modal-back-button"
              hitSlop={15}
              accessibilityLabel={formatMessage({ id: 'back' })}
              accessibilityRole="button"
            >
              <Icon variant="back" {...invertFontStyle} />
            </TouchableOpacity>
          ))}
      </View>
      {isLoading ? (
        <Skeleton width="1/3" h="$4" rounded="full" />
      ) : (
        <Text
          variant="headerTwo"
          style={modalHeaderStyles.modalHeader}
          accessibilityRole="header"
          accessibilityLevel={1}
          accessibilityLabel={headerText}
          focusable
          accessible
          {...invertFontStyle}
        >
          {headerText}
        </Text>
      )}
      <View style={modalHeaderStyles.headerFlexEnd}>
        {customHeaderRight ||
          (dismissType === 'close' && (
            <TouchableOpacity
              onPress={() => onDismiss?.()}
              testID="modal-close-button"
              hitSlop={15}
              accessibilityLabel={formatMessage({ id: 'close' })}
              accessibilityRole="button"
            >
              <Icon variant="cancel" {...invertFontStyle} />
            </TouchableOpacity>
          ))}
      </View>
    </View>
  );
};

export const modalHeaderStyles = StyleSheet.create({
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.$4,
    // ios is already "card style".
    ...(!isIos && {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
    }),
  },
  headerFlexStart: {
    flex: 1,
    alignItems: 'flex-start',
  },
  modalHeader: {
    marginBottom: 0,
  },
  headerFlexEnd: {
    flex: 1,
    alignItems: 'flex-end',
  },
});

/* handle a potential situation for deeplinks to a closed app (that seems to have been solved but could come back) where you can't navigate back */
export const useGetModalDismiss = (dismissType: 'close' | 'back' = 'back') => {
  const router = useRouter();

  return useCallback(() => {
    router.canGoBack() ? router.dismiss() : router.replace(routes.base);
  }, [router]);
};
